import { useCallback, useEffect, useState } from 'react'
import { useDpopFetch } from './useDpopFetch'

// useOrders — fetches the seller-orders list for a given shop handle from
// GET /v1/shops/:handle/seller-orders. Returns { orders, refresh, status }.
//
// Auth: v2 JWT via resolveShopAuth (headlo-worker/src/routes/shops.ts).
// Wave 4a.5 added the v2 branch to that resolver, so authFetch (DPoP)
// on the mobile side is enough — no legacy magic token needed.
//
// Non-goals for v0.1:
//   - Pagination — endpoint returns full list; add server-side pagination
//     when a merchant's history grows past a few hundred orders
//   - Real-time push updates — push notifications (Wave 4e) will trigger
//     a refresh() call, but no websocket / polling
//   - Optimistic mutations — mark-shipped etc. (Wave 4c.3) will refresh
//     after the mutation returns rather than update the local list

const API_URL = (import.meta.env.VITE_HEADLO_API_URL as string | undefined) ?? 'https://api.headlo.com'

// Shape mirrors the worker's shopSellerOrdersHandler return. Kept aligned
// with `d:\headlo\headlo-repo\headlo-worker\src\routes\shops.ts:7981` — if
// the server shape changes, update here in lockstep.
export interface OrderItem {
  sku:            string | null
  product_name:   string
  amount_cents:   number
  quantity:       number
  product_type:   string
}

export interface Order {
  shop_order_id:       string
  buyer_email:         string
  product_type:        string
  amount_cents:        number | null
  created_at:          string
  tracking_number:     string | null
  shipped_at:          string | null
  shipping_address:    { name?: string; address?: { line1?: string; line2?: string; city?: string; state?: string; postal_code?: string; country?: string } } | null
  sku:                 string | null
  items:               OrderItem[]
  return_status:       string
  return_submitted_at: string | null
  return_tracking:     string | null
  review:              string | null
  review_rating:       number | null
  review_submitted_at: string | null
  received_at:         string | null
  refunded_at:         string | null
  refund_note:         string | null
  stripe_charge_id:    string | null
  /**
   * Origin of the order. `undefined` for native (platform.shop_order); the
   * Shopify normaliser in shops.ts sets this to 'shopify'. Used by the
   * detail screen to gate the Mark Shipped affordance — Shopify fulfillment
   * has to happen in Shopify, not via our seller-orders POST.
   */
  source?:             'shopify'
}

export type OrdersStatus =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'ready';   orders: Order[]; refreshing: boolean }
  | { status: 'error';   message: string }

export interface UseOrdersResult {
  state:   OrdersStatus
  refresh: () => void
}

export function useOrders(handle: string | null): UseOrdersResult {
  const authFetch = useDpopFetch()
  const [state, setState] = useState<OrdersStatus>({ status: 'idle' })
  const [tick,  setTick]  = useState(0)

  const refresh = useCallback(() => setTick(t => t + 1), [])

  useEffect(() => {
    if (!handle) {
      setState({ status: 'idle' })
      return
    }

    let alive = true

    // Preserve existing orders while refreshing so the list doesn't blank
    // out on every reload — better UX than a full-screen spinner on refresh.
    setState(prev => (prev.status === 'ready'
      ? { status: 'ready', orders: prev.orders, refreshing: true }
      : { status: 'loading' }))

    void (async () => {
      try {
        const url = `${API_URL.replace(/\/$/, '')}/v1/shops/${encodeURIComponent(handle)}/seller-orders`
        const res = await authFetch(url)
        if (!alive) return
        if (!res.ok) {
          const msg = await extractError(res)
          setState({ status: 'error', message: msg })
          return
        }
        const body = await res.json() as { orders: Order[] }
        setState({ status: 'ready', orders: body.orders ?? [], refreshing: false })
      } catch (e) {
        if (!alive) return
        setState({ status: 'error', message: (e as Error).message ?? String(e) })
      }
    })()

    return () => { alive = false }
  }, [handle, tick, authFetch])

  return { state, refresh }
}

async function extractError(res: Response): Promise<string> {
  try {
    const body = await res.json() as { error?: string }
    if (body?.error) return `${res.status}: ${body.error}`
  } catch { /* non-JSON body */ }
  return `${res.status} ${res.statusText}`
}

// ── Mark shipped ──────────────────────────────────────────────────────────
// POSTs to /v1/shops/:handle/orders/:order_id/ship with a tracking number.
// Native orders only — Shopify fulfillment lives on Shopify's side and
// would require a separate GraphQL mutation (deferred). The detail screen
// disables the button for source === 'shopify' with a "Manage in Shopify"
// hint instead.

export type MarkShippedState =
  | { status: 'idle' }
  | { status: 'submitting' }
  | { status: 'success'; order: Order }
  | { status: 'error';   message: string }

export interface UseMarkShippedResult {
  state:  MarkShippedState
  submit: (trackingNumber: string) => Promise<void>
  reset:  () => void
}

export function useMarkShipped(handle: string | null, orderId: string): UseMarkShippedResult {
  const authFetch = useDpopFetch()
  const [state, setState] = useState<MarkShippedState>({ status: 'idle' })

  const submit = useCallback(async (trackingNumber: string) => {
    if (!handle) {
      setState({ status: 'error', message: 'No shop selected' })
      return
    }
    const trimmed = trackingNumber.trim()
    if (!trimmed) {
      setState({ status: 'error', message: 'Enter a tracking number' })
      return
    }
    setState({ status: 'submitting' })
    try {
      const url = `${API_URL.replace(/\/$/, '')}/v1/shops/${encodeURIComponent(handle)}/orders/${encodeURIComponent(orderId)}/ship`
      const res = await authFetch(url, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ tracking_number: trimmed }),
      })
      if (!res.ok) {
        setState({ status: 'error', message: await extractError(res) })
        return
      }
      // Worker returns the updated order shape on success. If shape drifts
      // we still get a valid response — the caller refreshes the list next.
      const body = await res.json() as { order?: Order }
      setState({
        status: 'success',
        order:  body.order ?? {
          // Fallback synthetic order if the worker didn't echo one — the
          // detail screen only reads tracking_number + shipped_at from
          // the success payload, so this is enough.
          shop_order_id:       orderId,
          buyer_email:         '',
          product_type:        '',
          amount_cents:        null,
          created_at:          '',
          tracking_number:     trimmed,
          shipped_at:          new Date().toISOString(),
          shipping_address:    null,
          sku:                 null,
          items:               [],
          return_status:       'none',
          return_submitted_at: null,
          return_tracking:     null,
          review:              null,
          review_rating:       null,
          review_submitted_at: null,
          received_at:         null,
          refunded_at:         null,
          refund_note:         null,
          stripe_charge_id:    null,
        },
      })
    } catch (e) {
      setState({ status: 'error', message: (e as Error).message ?? String(e) })
    }
  }, [handle, orderId, authFetch])

  const reset = useCallback(() => setState({ status: 'idle' }), [])

  return { state, submit, reset }
}

// ── Derived helpers used by OrdersScreen ──────────────────────────────────

/** Composite status per order — matches how the merchant thinks about it. */
export type OrderState = 'needs-shipping' | 'shipped' | 'returned' | 'refunded'

export function deriveOrderState(order: Order): OrderState {
  if (order.refunded_at)              return 'refunded'
  if (order.return_status && order.return_status !== 'none') return 'returned'
  if (order.tracking_number || order.shipped_at)             return 'shipped'
  return 'needs-shipping'
}
